/**
 * Base class for all panels. All panels extend BasePanel and encapsulate
 * their UI elements, events, and initialization logic.
 */
import type JQuery from "jquery";

export type PanelContainer = JQuery<HTMLElement> | HTMLElement;

function isJQuery(el: PanelContainer): el is JQuery<HTMLElement> {
    return typeof (el as JQuery<HTMLElement>).addClass === "function";
}

export abstract class BasePanel {
    protected _initialized = false;
    protected _opened = false;
    // This is a self-referential type inside the class body; eslint's
    // no-use-before-define can still trigger depending on configuration.
    // eslint-disable-next-line no-use-before-define
    protected _parentPanel: BasePanel | null = null;

    /** Unique panel identifier (e.g. "info", "asset-manager") */
    abstract getId(): string;

    /** Root DOM element for the panel */
    abstract getContainer(): PanelContainer;

    /** Display the panel */
    show(): void {
        const container = this.getContainer();

        if (isJQuery(container)) {
            container.show();
        } else {
            container.style.display = "";
        }

        this._opened = true;
        this.onShow();
    }

    /** Hide the panel */
    hide(): void {
        const container = this.getContainer();

        if (isJQuery(container)) {
            container.hide();
        } else {
            container.style.display = "none";
        }

        this._opened = false;
        this.onHide();
    }

    /** Initialize UI and bind events. Called at app startup. */
    init(): void {
        if (this._initialized) {
            return;
        }

        this.onInit();
        this._initialized = true;
    }

    /** Override: called when panel is shown */
    protected onShow(): void {
        // Subclasses override if needed
    }

    /** Override: called when panel is hidden */
    protected onHide(): void {
        // Subclasses override if needed
    }

    /** Override: called during init() for first-time setup */
    protected onInit(): void {
        // Subclasses override if needed
    }

    // --- Optional methods for panels with backend data ---

    /** Override: returns true if panel has unsaved changes */
    hasUnsavedChanges(): boolean {
        return false;
    }

    /** Override: prompt user to confirm discard; returns true if confirmed */
    confirmDiscardChanges(): boolean {
        return true;
    }

    /** Override: persist changes to backend */
    async save(): Promise<void> {
        // Subclasses override if needed
    }

    /** Override: revert unsaved changes */
    discard(): void {
        // Subclasses override if needed
    }

    /** Override: reset panel state to default view/state */
    reset(): void {
        // Subclasses override if needed
    }

    setParentPanel(parent: BasePanel | null): void {
        this._parentPanel = parent;
    }

    getParentPanel(): BasePanel | null {
        return this._parentPanel;
    }

    /** Returns true when this panel is currently opened. */
    isOpen(): boolean {
        return this._opened;
    }
}
